import { useCallback, useState } from 'react';
import {
  clearLoyaltyActivatedCouponHandoff,
  loyaltyActivatedCouponStorageKey,
} from 'pi-kiosk-shared/loyalty/loyaltySessionHandoff';
import { clearPromoActivatedRewardHandoff } from 'pi-kiosk-shared/promo/promoSessionHandoff';
import { readActivatedPromoRewardHandoff } from './readActivatedPromoRewardHandoff.js';

export type CustomerPromoExclusiveChoice = 'promo' | 'loyalty' | null;

/** i18n key — remove-code failed; promo stays visible (do not pretend loyalty-only). */
export const CUSTOMER_PROMO_EXCLUSIVE_CHOOSE_LOYALTY_RELEASE_FAILED_KEY =
  'promo.exclusive.releaseFailed';

function readActivatedCouponHandoff(
  tenantCode: string,
  checkoutSessionId: string | null,
): string | null {
  if (checkoutSessionId == null || checkoutSessionId.length === 0) {
    return null;
  }
  if (typeof sessionStorage === 'undefined') {
    return null;
  }
  try {
    const value = sessionStorage.getItem(
      loyaltyActivatedCouponStorageKey(tenantCode, checkoutSessionId),
    );
    return value != null && value.length > 0 ? value : null;
  } catch {
    return null;
  }
}

export function useCustomerPromoExclusiveConflict(input: {
  readonly stackingMode: 'EXCLUSIVE' | 'STACK_PROMO_THEN_LOYALTY';
  readonly tenantCode: string;
  readonly checkoutSessionId: string | null;
  /**
   * Soft-reserve release (POST /remove-code) — same path as abandon.
   * Must resolve successfully before loyalty-only; rejection keeps promo visible.
   */
  readonly onReleasePromoCode?: (
    checkoutSessionId: string,
  ) => void | Promise<void>;
  /** Clear typed applied chip after successful choose-loyalty remove. */
  readonly onClearTypedPromo?: () => void;
}): {
  readonly showExclusiveModal: boolean;
  readonly effectiveActivatedCouponId: string | null;
  readonly effectiveActivatedPromoRewardId: string | null;
  readonly choosePromo: () => void;
  readonly chooseLoyalty: () => Promise<void>;
  readonly closeExclusiveModal: () => void;
  readonly isChoosingLoyalty: boolean;
  readonly chooseLoyaltyErrorKey: string | null;
} {
  const {
    stackingMode,
    tenantCode,
    checkoutSessionId,
    onReleasePromoCode,
    onClearTypedPromo,
  } = input;

  const activatedCouponId = readActivatedCouponHandoff(tenantCode, checkoutSessionId);
  const activatedPromoRewardId = readActivatedPromoRewardHandoff(tenantCode, checkoutSessionId);
  const conflictKey =
    stackingMode === 'EXCLUSIVE' &&
    activatedCouponId !== null &&
    activatedPromoRewardId !== null
      ? `${activatedCouponId}:${activatedPromoRewardId}`
      : null;
  const [resolutionState, setResolutionState] = useState<{
    readonly key: string;
    readonly choice: CustomerPromoExclusiveChoice;
    readonly dismissed: boolean;
  } | null>(null);
  const [isChoosingLoyalty, setIsChoosingLoyalty] = useState(false);
  const [chooseLoyaltyErrorKey, setChooseLoyaltyErrorKey] = useState<string | null>(null);
  /** Sticky promo-only so create can send activatedCouponId: null (BE session may still list coupon). */
  const [clearedLoyaltyForPromo, setClearedLoyaltyForPromo] = useState(false);
  const resolution =
    conflictKey !== null && resolutionState?.key === conflictKey
      ? resolutionState
      : { key: conflictKey ?? '', choice: null as CustomerPromoExclusiveChoice, dismissed: false };

  const hasConflict = conflictKey !== null;
  const showExclusiveModal = hasConflict && !resolution.dismissed && resolution.choice === null;

  const choosePromo = useCallback((): void => {
    if (conflictKey === null) {
      return;
    }
    setChooseLoyaltyErrorKey(null);
    setClearedLoyaltyForPromo(true);
    setResolutionState({ key: conflictKey, choice: 'promo', dismissed: true });
    if (checkoutSessionId != null && checkoutSessionId.length > 0) {
      clearLoyaltyActivatedCouponHandoff(tenantCode, checkoutSessionId);
    }
  }, [conflictKey, checkoutSessionId, tenantCode]);

  const chooseLoyalty = useCallback(async (): Promise<void> => {
    if (conflictKey === null || isChoosingLoyalty) {
      return;
    }
    setChooseLoyaltyErrorKey(null);
    const sessionId = checkoutSessionId;

    setIsChoosingLoyalty(true);
    try {
      if (sessionId != null && sessionId.length > 0 && onReleasePromoCode) {
        await onReleasePromoCode(sessionId);
      }
      // Only after successful remove (or no release callback): loyalty-only + clear chip.
      setClearedLoyaltyForPromo(false);
      setResolutionState({ key: conflictKey, choice: 'loyalty', dismissed: true });
      if (sessionId != null && sessionId.length > 0) {
        clearPromoActivatedRewardHandoff(tenantCode, sessionId);
      }
      onClearTypedPromo?.();
    } catch {
      // Keep promo visible — do not clear handoff or treat as loyalty-only.
      setChooseLoyaltyErrorKey(CUSTOMER_PROMO_EXCLUSIVE_CHOOSE_LOYALTY_RELEASE_FAILED_KEY);
    } finally {
      setIsChoosingLoyalty(false);
    }
  }, [
    conflictKey,
    checkoutSessionId,
    isChoosingLoyalty,
    onClearTypedPromo,
    onReleasePromoCode,
    tenantCode,
  ]);

  const closeExclusiveModal = useCallback((): void => {
    if (conflictKey === null) {
      return;
    }
    setResolutionState({ key: conflictKey, choice: null, dismissed: true });
  }, [conflictKey]);

  let effectiveActivatedCouponId = activatedCouponId;
  let effectiveActivatedPromoRewardId = activatedPromoRewardId;
  if (clearedLoyaltyForPromo || (hasConflict && resolution.choice === 'promo')) {
    effectiveActivatedCouponId = null;
  } else if (hasConflict && resolution.choice === 'loyalty') {
    effectiveActivatedPromoRewardId = null;
  } else if (hasConflict && !resolution.dismissed) {
    effectiveActivatedCouponId = null;
    effectiveActivatedPromoRewardId = null;
  }

  return {
    showExclusiveModal,
    effectiveActivatedCouponId,
    effectiveActivatedPromoRewardId,
    choosePromo,
    chooseLoyalty,
    closeExclusiveModal,
    isChoosingLoyalty,
    chooseLoyaltyErrorKey,
  };
}
